import { useMemo } from "react";

import { environment } from "@/environments/environment";
import { chatsKey } from "@/shared/constants/queryKeys";
import {
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import type {
  ChatStatus,
  ChatStatusCountsResponse
} from "@/features/chat/domain/repositories/chat";
import { chatDataSourceImplementation } from "@/features/chat/infrastructure/datasources/chat/chatDataSourceImplementation";
import { httpImplementation } from "@/shared/services/http";
import {
  getChats,
  getStatusCounts,
  markChatAsRead
} from "@/features/chat/shared/services";
import type { Chat, ChatListResponse } from "@/features/chat/shared/types";

// Reusable hook to get the memoized chatDataSource
const useChatDataSource = () => {
  const httpClient = useMemo(
    () =>
      httpImplementation({
        baseURL: environment.styxApiUrl,
        withInterceptor: true
      }),
    []
  );
  const chatDataSource = useMemo(
    () => chatDataSourceImplementation(httpClient),
    [httpClient]
  );
  return chatDataSource;
};

export const useChatsInfiniteQuery = (
  pageSize = 6,
  query?: string | null,
  status?: ChatStatus | null,
  lastMessageOpts: "full" | "light" = "light"
) => {
  const chatDataSource = useChatDataSource();

  return useInfiniteQuery({
    queryKey: [...chatsKey, { pageSize, query, status, lastMessageOpts }],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const cursorParam = pageParam ?? null;
      const queryParam = query ?? null;
      const statusParam = status ?? null;

      if (environment.useChatStub) {
        return await getChats(
          cursorParam,
          pageSize,
          queryParam,
          statusParam,
          lastMessageOpts
        );
      }

      return await chatDataSource.getChats<ChatListResponse>({
        cursor: cursorParam ?? undefined,
        limit: pageSize,
        query: queryParam ?? undefined,
        status: statusParam ?? undefined,
        last_message_opts: lastMessageOpts
      });
    },
    getNextPageParam: (lastPage) => {
      const isAxiosResponse = "status" in lastPage && "config" in lastPage;

      const cursor = isAxiosResponse
        ? (lastPage as unknown as { data: ChatListResponse }).data.cursor
        : (lastPage as ChatListResponse).cursor;

      return cursor || undefined;
    },
    select: (
      data
    ): {
      chats: ChatListResponse["data"];
      totalCount: number;
      counts?: ChatListResponse["counts"];
    } => {
      const chats = data.pages.flatMap((page) => {
        // Check if it's an Axios response  for switch between stub and api
        const isAxiosResponse = "status" in page && "config" in page;

        if (isAxiosResponse) {
          const axiosPage = page as unknown as { data: ChatListResponse };
          const chatsData = axiosPage.data.data || [];
          return chatsData;
        } else {
          const stubPage = page as ChatListResponse;
          return stubPage.data || [];
        }
      });

      const firstPage = data.pages[0];
      // Checking if it's an Axios response for switch between stub and api
      const isAxiosResponse = "status" in firstPage && "config" in firstPage;
      const totalCount = isAxiosResponse
        ? (firstPage as unknown as { data: ChatListResponse }).data.count ?? 0
        : (firstPage as ChatListResponse).count ?? 0;

      const counts = isAxiosResponse
        ? (firstPage as unknown as { data: ChatListResponse }).data.counts
        : (firstPage as ChatListResponse).counts;

      return { chats, totalCount, counts };
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
};

// update a page by updating the nickname of a matching chat user
const updateChatNickname = (
  page: any,
  userId: string,
  nickname: string | null
) => {
  if (!page) return page;

  const isAxiosResponse = "status" in page && "config" in page;
  const pageData = isAxiosResponse
    ? (page as unknown as { data: ChatListResponse }).data
    : (page as ChatListResponse);

  if (!pageData || !Array.isArray(pageData.data)) return page;

  const updatedData = pageData.data.map((chat: Chat) => {
    if (!chat?.id) return chat;
    const [, participantId] = chat.id.split("#");
    return participantId === userId
      ? { ...chat, user: { ...chat.user, nickname: nickname ?? undefined } }
      : chat;
  });

  const updatedPage = { ...pageData, data: updatedData };
  return isAxiosResponse ? { ...page, data: updatedPage } : updatedPage;
};

export const isChatListQuery = (query: { queryKey: readonly unknown[] }) =>
  query.queryKey.length > chatsKey.length &&
  query.queryKey[chatsKey.length] !== "messages" &&
  query.queryKey[chatsKey.length] !== "status-counts";

// Helper to optimistically update chat user nickname in all infinite chat list queries
export const updateChatNicknameOptimistically = (
  queryClient: QueryClient,
  userId: string,
  nickname: string | null
) => {
  queryClient.setQueriesData(
    {
      queryKey: chatsKey,
      predicate: isChatListQuery
    },
    (oldData: any) => {
      if (!oldData?.pages || !Array.isArray(oldData.pages)) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) =>
          updateChatNickname(page, userId, nickname)
        )
      };
    }
  );
};

// update a page by marking matching chat as read
const updateChatAsRead = (page: any, userId: string) => {
  if (!page) {
    return page;
  }

  const isAxiosResponse = "status" in page && "config" in page;
  const pageData = isAxiosResponse
    ? (page as unknown as { data: ChatListResponse }).data
    : (page as ChatListResponse);

  // Safety check: ensure pageData exists and has the expected structure
  if (!pageData || !Array.isArray(pageData.data)) {
    return page;
  }

  const updatedData = pageData.data.map((chat: Chat) => {
    // Safety check: ensure chat has an id
    if (!chat?.id) {
      return chat;
    }
    const [, participantId] = chat.id.split("#");
    return participantId === userId ? { ...chat, is_read: true } : chat;
  });

  const updatedPage = { ...pageData, data: updatedData };
  return isAxiosResponse ? { ...page, data: updatedPage } : updatedPage;
};

// Helper function to update chat as read optimistically
export const updateChatAsReadOptimistically = (
  queryClient: QueryClient,
  userId: string
) => {
  // Update unread count
  queryClient.setQueryData<ChatStatusCountsResponse>(
    [...chatsKey, "status-counts"],
    (old) => (old ? { ...old, unread: Math.max(0, old.unread - 1) } : old)
  );

  queryClient.setQueriesData(
    {
      queryKey: chatsKey,
      predicate: isChatListQuery
    },
    (oldData: any) => {
      if (!oldData?.pages || !Array.isArray(oldData.pages)) {
        return oldData;
      }

      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => updateChatAsRead(page, userId))
      };
    }
  );
};

export const useChatStatusCountsQuery = () => {
  const chatDataSource = useChatDataSource();

  return useQuery({
    queryKey: [...chatsKey, "status-counts"],
    queryFn: async () => {
      if (environment.useChatStub) {
        return await getStatusCounts();
      }

      const response =
        await chatDataSource.getStatusCounts<ChatStatusCountsResponse>();
      // Checking if it's an Axios response for switch between stub and api
      const isAxiosResponse = "status" in response && "config" in response;
      if (isAxiosResponse) {
        const axiosData = (
          response as unknown as { data: ChatStatusCountsResponse }
        ).data;

        if (axiosData && typeof axiosData === "object" && "data" in axiosData) {
          return (axiosData as unknown as { data: ChatStatusCountsResponse })
            .data;
        }
        return axiosData;
      }

      return response;
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
};

export const useMarkChatAsReadMutation = () => {
  const chatDataSource = useChatDataSource();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      if (environment.useChatStub) {
        await markChatAsRead(userId);
        return;
      }
      const result = await chatDataSource.markChatAsRead(userId);
      return result;
    },
    onSuccess: (_, userId) => {
      updateChatAsReadOptimistically(queryClient, userId);
      void queryClient.invalidateQueries({
        queryKey: [...chatsKey, "status-counts"]
      });
    }
  });
};
